import React, { useEffect, useRef, useState } from "react";
import { Paperclip, Upload, FileText, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

const usePreview = (a) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/files/${a.path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(""));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [a.path]);
  return url;
};

const AttItem = ({ a, canDelete, onDelete }) => {
  const url = usePreview(a);
  const img = a.content_type.startsWith("image/");
  return (
    <div data-testid={`att-item-${a.id}`} className="rounded-lg border border-border overflow-hidden bg-card">
      <button type="button" onClick={() => url && window.open(url)} className="block w-full h-32 bg-muted grid place-items-center hover:opacity-90 transition-opacity" title="Open">
        {!url ? <Loader2 size={18} className="animate-spin text-muted-foreground" /> : img ? <img src={url} alt={a.filename} className="h-32 w-full object-cover" />
          : <span className="flex flex-col items-center gap-1 text-destructive"><FileText size={30} /><span className="text-xs font-bold">PDF</span></span>}
      </button>
      <div className="p-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate" title={a.filename}>{a.filename}</div>
          <div className="text-[10px] text-muted-foreground truncate">{a.uploaded_by_name} · {fmt(a.created_at)}</div>
        </div>
        {canDelete && <button data-testid={`att-del-${a.id}`} onClick={() => onDelete(a)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>}
      </div>
    </div>
  );
};

export const AttachmentsDialog = ({ open, onClose, entityType, entityId, title, onChanged }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    if (open) { setItems(null); load(); }
  }, [open, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (!/^image\/|application\/pdf/.test(f.type)) { toast.error(`${f.name}: only photos or PDF`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max 10MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/attachments", fd, { params: { entity_type: entityType, entity_id: entityId }, headers: { "Content-Type": "multipart/form-data" } }); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    setBusy(false); if (input.current) input.current.value = "";
    load(); onChanged?.();
  };
  const del = async (a) => {
    if (!window.confirm(`Remove ${a.filename}?`)) return;
    try { await api.delete(`/attachments/${a.id}`); load(); onChanged?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canDel = (a) => (entityType === "fuel" ? (user?.tenant_roles?.includes("admin") || user?.platform_role === "superadmin" || a.uploaded_by === user?.id) : a.can_delete);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="attachments-dialog" className="bg-card sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip size={16} /> Attachments{title ? ` · ${title}` : ""}</DialogTitle></DialogHeader>
        {hasWrite(user) && (
          <label data-testid="att-upload" className="flex items-center justify-center gap-2 h-20 rounded-lg border-2 border-dashed border-input text-sm text-muted-foreground cursor-pointer hover:border-primary hover:text-primary transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? "Uploading…" : "Add photo or PDF (tap to choose or take photo)"}
            <input ref={input} data-testid="att-file-input" type="file" accept="image/*,application/pdf" multiple className="hidden" disabled={busy} onChange={(e) => upload([...e.target.files])} />
          </label>
        )}
        {items === null ? <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-muted-foreground" /></div>
          : items.length === 0 ? <p data-testid="att-empty" className="text-sm text-muted-foreground text-center py-6">No attachments yet.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{items.map((a) => <AttItem key={a.id} a={a} canDelete={hasWrite(user) && canDel(a)} onDelete={del} />)}</div>}
      </DialogContent>
    </Dialog>
  );
};

export const uploadAttachments = async (entityType, entityId, files) => {
  let ok = 0;
  for (const f of files) {
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/attachments", fd, { params: { entity_type: entityType, entity_id: entityId }, headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
    catch (e) { toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail)}`); }
  }
  if (ok) toast.success(`${ok} attachment(s) saved`);
};

export const AttachPicker = ({ files, setFiles, testid = "att-pick" }) => {
  const add = (list) => {
    const good = [...list].filter((f) => {
      if (!/^image\/|application\/pdf/.test(f.type)) { toast.error(`${f.name}: only photos or PDF`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max 10MB`); return false; }
      return true;
    });
    setFiles([...files, ...good]);
  };
  return (
    <div data-testid={testid}>
      <label className="block text-sm font-medium mb-1.5">Bill / photo / PDF (optional)</label>
      <label className="flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed border-input text-sm text-muted-foreground cursor-pointer hover:border-primary hover:text-primary transition-colors">
        <Paperclip size={15} /> Attach photo or PDF
        <input data-testid={`${testid}-input`} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </label>
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <span key={i} data-testid={`${testid}-file-${i}`} className="inline-flex items-center gap-1 max-w-[220px] rounded bg-primary/10 text-primary text-xs px-2 py-1">
              <FileText size={12} /><span className="truncate">{f.name}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="hover:text-destructive"><Trash2 size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const AttachButton = ({ count, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} title={count ? `${count} attachment(s)` : "Attachments"}
    className={`h-7 px-1.5 rounded-md flex items-center gap-0.5 text-xs transition-colors ${count ? "text-primary bg-primary/10 hover:bg-primary/20 font-semibold" : "text-muted-foreground hover:text-primary"}`}>
    <Paperclip size={14} />{count ? <span className="num">{count}</span> : null}
  </button>
);
